import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Box,
  TextField,
  InputAdornment,
  MenuItem,
  TablePagination,
  Button,
  Popover,
  Typography,
  IconButton,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { CampaignActionMenuTable } from "../.";
import Scrollbar from "../../../custom-scroll/Scrollbar";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { es } from 'date-fns/locale';
import { useState } from "react";
import PropTypes from "prop-types";
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import CloseIcon from '@mui/icons-material/Close'; 
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { CAMPAIGN_STATUS_COLORS } from "../constants";
import { formatDateTime, getProgrammingDateDisplay } from "../../../../utils/dateFormatters-campaign.utils";

const headCellSx = {
  fontWeight: 600,
  color: 'text.secondary',
  fontSize: '0.8rem',
  letterSpacing: '0.04em',
  textTransform: 'uppercase',
  borderBottom: '2px solid',
  borderColor: 'divider',
};

export const CampaignTable = ({
  campaigns = [],
  loading = false,
  searchTerm = '',
  statusFilter = 'todos',
  dateRange = { from: null, to: null },
  onSearchChange,
  onStatusFilterChange,
  onDateRangeChange,
  page = 1,
  pageSize = 10,
  total = 0,
  onPageChange,
  onPageSizeChange,

  onViewCampaign,
  onEditCampaign,
  onDeleteCampaign,
  onLaunchCampaign,
  onCloneCampaign,
}) => {
  const [datePickerAnchor, setDatePickerAnchor] = useState(null);
  const [tempDateRange, setTempDateRange] = useState({ from: null, to: null });

  const getStatusColor = (estado) => {
    return CAMPAIGN_STATUS_COLORS[estado] || 'default';
  };

  const formatDate = (dateString) => {
    const options = { year: "numeric", month: "long", day: "numeric" };
    return new Date(dateString).toLocaleDateString("es-ES", options);
  };

  const renderProgrammingDate = (campaign) => {
    const prog = getProgrammingDateDisplay(campaign.fechaProgramacion, campaign.estado);

    if (prog.type === "scheduled") {
      return (
        <Chip
          icon={<CalendarTodayIcon />}
          label={formatDateTime(prog.date)}
          color="primary"
          size="small"
          sx={{ fontWeight: "medium" }}
        />
      );
    }

    if (prog.type === "pending") {
      return (
        <Typography variant="body2" color="success.main" fontWeight={500}>
          lista para enviarse
        </Typography>
      );
    }

    return (
      <Typography variant="body2" color="text.secondary">
        -- -- -- --
      </Typography>
    );
  };

// range date
  const handleOpenDatePicker = (event) => {
    setTempDateRange(dateRange);
    setDatePickerAnchor(event.currentTarget);
  };


  const handleCloseDatePicker = () => {
    setDatePickerAnchor(null);
  };

  const handleApplyDateFilter = () => {
    onDateRangeChange(tempDateRange);
    handleCloseDatePicker();
  };


  const handleClearDateFilter = () => {
    setTempDateRange({ from: null, to: null });
    onDateRangeChange({ from: null, to: null });
    handleCloseDatePicker();
  };


  const getDateFilterLabel = () => {
    if (!dateRange.from && !dateRange.to) {
      return 'Filtrar por fecha';
    }

    const formatShort = (date) => {
      const d = new Date(date);
      return `${d.getDate()}/${d.getMonth() + 1}/${d.getFullYear()}`;
    };

    if (dateRange.from && dateRange.to) {
      return `${formatShort(dateRange.from)} - ${formatShort(dateRange.to)}`;
    }

    if (dateRange.from) {
      return `Desde ${formatShort(dateRange.from)}`;
    }

    return `Hasta ${formatShort(dateRange.to)}`;
  };

  const isDatePickerOpen = Boolean(datePickerAnchor);
  const hasDateFilter = dateRange.from || dateRange.to;


  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%", minHeight: 0 }}>
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          justifyContent: { xs: "flex-start", sm: "space-between" },
          alignItems: { xs: "stretch", sm: "center" },
          mt: 1,
          mb: 2,
          gap: 2,
          flex: "0 0 auto",
        }}
      >
        <Box sx={{ display: "flex", width: { xs: "100%", sm: "60%" }, gap: 2}}>
          <TextField
          sx={{ width: "65%"}}
            placeholder="Buscar por nombre o alias"
            value={searchTerm}
            onChange={(e) => onSearchChange(e.target.value)}
            size="small"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />

          <Box sx={{ width: '35%', position: 'relative' }}>
            <Button
              fullWidth
              variant="outlined"
              size="small"
              startIcon={<CalendarTodayIcon />}
              endIcon={
                hasDateFilter && (
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleClearDateFilter();
                    }}
                    sx={{ ml: 'auto', p: 0 }}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                )
              }
              onClick={handleOpenDatePicker}
              sx={{
                justifyContent: 'flex-start',
                textTransform: 'none',
                color: hasDateFilter ? 'primary.main' : 'text.secondary',
                borderColor: hasDateFilter ? 'primary.main' : 'divider',
                fontWeight: hasDateFilter ? 600 : 400,
                height: '40px',
              }}
            >
              <Typography
                variant="body2"
                noWrap
                sx={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  flex: 1,
                  textAlign: 'left',
                }}
              >
                {getDateFilterLabel()}
              </Typography>
            </Button>

            <Popover
              open={isDatePickerOpen}
              anchorEl={datePickerAnchor}
              onClose={handleCloseDatePicker}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'left',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'left',
              }}
            >
              <Box sx={{ p: 2, minWidth: 300 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Seleccionar rango de fechas
                </Typography>

                <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={es}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>

                    <DatePicker
                      label="Desde"
                      value={tempDateRange.from}
                      onChange={(newValue) =>
                        setTempDateRange((prev) => ({ ...prev, from: newValue }))
                      }
                      maxDate={tempDateRange.to || Date.now()}
                      slotProps={{
                        textField: {
                          size: 'small',
                          fullWidth: true,
                        },
                      }}
                    />

                    <DatePicker
                      label="Hasta"
                      value={tempDateRange.to}
                      onChange={(newValue) =>
                        setTempDateRange((prev) => ({ ...prev, to: newValue }))
                      }
                      minDate={tempDateRange.from || undefined}
                      maxDate={Date.now()}
                      slotProps={{
                        textField: {
                          size: 'small',
                          fullWidth: true,
                        },
                      }}
                    />
                  </Box>
                </LocalizationProvider>

                <Box sx={{ display: 'flex', gap: 1, mt: 2, justifyContent: 'flex-end' }}>
                  <Button size="small" onClick={handleClearDateFilter}>
                    Limpiar
                  </Button>
                  <Button
                    size="small"
                    variant="contained"
                    onClick={handleApplyDateFilter}
                    disabled={!tempDateRange.from && !tempDateRange.to}
                  >
                    Aplicar
                  </Button>
                </Box>
              </Box>
            </Popover>
          </Box>
        </Box>

        <Box sx={{ width: { xs: "100%", sm: 250 } }}>
          <TextField
            select
            fullWidth
            label="Filtrar por estado"
            value={statusFilter}
            onChange={(e) => onStatusFilterChange(e.target.value)}
            size="small"
          >
            <MenuItem value="todos">Todos los estados</MenuItem>
            <MenuItem value="SCHEDULED">Programado</MenuItem>
            <MenuItem value="PENDING">Pendiente</MenuItem>
            <MenuItem value="COMPLETED">Completado</MenuItem>
            <MenuItem value="LAUNCHED">Lanzado</MenuItem>
            <MenuItem value="CANCELLED">Cancelado</MenuItem>
          </TextField>
        </Box>
      </Box>

      <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
      <Paper
        sx={{
          width: '100%',
          maxWidth: 1200,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          flex: '1 1 auto',
          minHeight: 0,
          borderRadius: 2,
        }}
      >
        <TableContainer
          component={Scrollbar}
          sx={{
            flex: "1 1 auto",
            minHeight: 0,
          }}
        >
          <Table
            stickyHeader
            sx={{ minWidth: 1000 }}
            aria-label="tabla de campañas"
          >
            <TableHead>
              <TableRow sx={{ backgroundColor: 'background.default' }}>
                <TableCell sx={headCellSx}>Nombre de Campaña</TableCell>
                <TableCell align="center" sx={headCellSx}>Estado</TableCell>
                <TableCell sx={headCellSx}>Fecha de Creación</TableCell>
                <TableCell sx={headCellSx}>Fecha Programada</TableCell>
                <TableCell align="center" sx={headCellSx}>Acciones</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                    <Box sx={{ color: "text.secondary" }}>
                      Cargando campañas...
                    </Box>
                  </TableCell>
                </TableRow>
              ) : campaigns.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                    <Box sx={{ color: "text.secondary" }}>
                      {searchTerm || statusFilter !== "todos"
                        ? "No se encontraron campañas con los filtros aplicados"
                        : "No hay campañas disponibles por el momento"}
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (
                campaigns.map((campaign) => (
                  <TableRow
                    key={campaign.id}
                    hover
                    sx={{
                      '&:last-child td': { borderBottom: 0 },
                      transition: 'background-color 0.15s',
                    }}
                  >
                    <TableCell component="th" scope="row">
                      <Typography variant="body2">{campaign.nombre}</Typography>
                      {campaign.alias && (
                        <Typography variant="caption" color="text.secondary">
                          {campaign.alias}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={campaign.estado}
                        color={getStatusColor(campaign.estado)}
                        size="small"
                        sx={{
                          fontWeight: "medium",
                          minWidth: 90,
                        }}
                      />
                    </TableCell>
                    <TableCell>{formatDate(campaign.fechaCreacion)}</TableCell>
                    <TableCell>{renderProgrammingDate(campaign)}</TableCell>
                    <TableCell align="center">
                      <CampaignActionMenuTable
                        campaign={campaign}
                        onView={onViewCampaign}
                        onEdit={onEditCampaign}
                        onDelete={onDeleteCampaign}
                        onLaunch={onLaunchCampaign}
                        onClone={onCloneCampaign}
                      />                    
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={total}
          page={page - 1} 
          onPageChange={(e, newPage) => onPageChange(newPage + 1)}
          rowsPerPage={pageSize}
          onRowsPerPageChange={(e) => {
            const newSize = parseInt(e.target.value, 10);
            onPageSizeChange(newSize);
          }}
          sx={{
            flex: "0 0 auto",
            overflow: "hidden",
          }}
          labelDisplayedRows={({ from, to, count }) =>
            `${from}-${to} de ${count !== -1 ? count : `más de ${to}`}`
          }
          labelRowsPerPage="Filas por página:"
          rowsPerPageOptions={[5, 10, 25, 50, 100]}
        />
      </Paper>
      </Box>
    </Box>
  );
};

CampaignTable.propTypes = {
  campaigns: PropTypes.array,
  loading: PropTypes.bool,
  searchTerm: PropTypes.string,
  statusFilter: PropTypes.string,
  dateRange: PropTypes.shape({
    from: PropTypes.instanceOf(Date),
    to: PropTypes.instanceOf(Date),
  }),
  onSearchChange: PropTypes.func.isRequired,
  onStatusFilterChange: PropTypes.func.isRequired,
  onDateRangeChange: PropTypes.func.isRequired,
  page: PropTypes.number,
  pageSize: PropTypes.number,
  total: PropTypes.number,
  onPageChange: PropTypes.func.isRequired,
  onPageSizeChange: PropTypes.func.isRequired,
  onViewCampaign: PropTypes.func,
  onEditCampaign: PropTypes.func,
  onDeleteCampaign: PropTypes.func,
  onLaunchCampaign: PropTypes.func,
  onCloneCampaign: PropTypes.func,
};
